import React, { Fragment } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useHistory } from "react-router-dom";

import { useUserStore, useAccountStore } from "base/stores";
import { ROLES } from "base/constants";

import { Link } from "../../navigation/link";
import { GroupIcon } from "@/icons";

import styles from "./usericon.css";

export const AVATAR_SIZE = {
  SIZE16: "size16",
  SIZE20: "size20",
  SIZE24: "size24",
  SIZE28: "size28",
  SIZE32: "size32",
  SIZE36: "size36",
  SIZE44: "size44",
  SIZE64: "size64",
  SIZE96: "size96",
  SIZE120: "size120"
};

function extractAvatarChar(name) {
  return name
    .split(" ")
    .filter((part) => part)
    .map((part) => part[0].toUpperCase())
    .slice(0, 2)
    .join("");
}

function getAvatarColor(name = "") {
  const charCode = name.trim().toLowerCase().charCodeAt(0);
  if (charCode >= 97 && charCode <= 100) {
    return "green";
  } else if (charCode >= 101 && charCode <= 104) {
    return "blue";
  } else if (charCode >= 105 && charCode <= 108) {
    return "magenta";
  } else if (charCode >= 109 && charCode <= 112) {
    return "purple";
  } else if (charCode >= 113 && charCode <= 116) {
    return "orange";
  } else if (charCode >= 117 && charCode <= 122) {
    return "blue";
  }
}

export function UserIcon(props) {
  const {
    userId,
    name,
    size = AVATAR_SIZE.SIZE20,
    className = "",
    enableLink = false,
    onMouseEnter,
    onMouseLeave,
    disabled = false,
    kind = ROLES.USER
  } = props;

  const accountId = useAccountStore.getState()._id;
  const appConfig = useAccountStore.getState().AppConfig;

  const users = useUserStore.getState().users || {};
  const user = users[userId] || {};

  const userName = (user && user.Name) || name || "Unknown";
  let lastUpdate = user && user.lastUpdate;

  const getImageProfileUrl = `${appConfig.ProfileBasePath}/${accountId}/${userId}/photos/130x130.jpeg?${lastUpdate}`;

  const generateAvatarName =
    size === AVATAR_SIZE.SIZE16 || size === AVATAR_SIZE.SIZE20
      ? userName[0]
      : extractAvatarChar(userName);

  let userIconClass = classNames(
    styles.imageFallback,
    styles.userProfileImage,
    styles.textAvatar,
    styles.userThumb,
    styles.figureWrapper,
    styles[getAvatarColor(userName)],
    styles[size],
    {
      [styles.disabledState]: disabled
    }
  );

  const fallBackError = classNames(
    styles.imageFallback,
    styles[getAvatarColor(userName)],
    styles.errorImage
  );

  const onErrorImageFallback = (e) => {
    if (e) {
      e.target.className = fallBackError;
    }
  };

  const history = useHistory();
  const USER_BADGE = {
    Group: () => <GroupIcon className={`${styles[size]} ${styles.userThumb}`} />
  };

  const userProfilePicture = () => {
    return (
      <figure className={`${userIconClass}`} data-username={generateAvatarName}>
        <img
          src={getImageProfileUrl}
          loading="lazy"
          alt={""}
          className={styles.imageIcon}
          onError={onErrorImageFallback}
        />
      </figure>
    );
  };

  const IconRenderer = USER_BADGE[kind];
  const userIcon = IconRenderer || userProfilePicture;

  return (
    <div
      className={`${className}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {enableLink ? (
        <Link
          onClick={() => history.push(`/account/user/profile/${userId}`)}
          className={styles.userProfileImage}
        >
          {userProfilePicture()}
        </Link>
      ) : (
        <Fragment>{userIcon && userIcon()}</Fragment>
      )}
    </div>
  );
}

UserIcon.propTypes = {
  userId: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.number,
  actions: PropTypes.array,
  enableLink: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  disabled: PropTypes.bool,
  kind: PropTypes.string
};
